import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useField } from 'formik';
import moment from 'moment';
import styled from 'styled-components';
import FocusLock from 'react-focus-lock';

import '@spotify-internal/react-dates/initialize';
import '@spotify-internal/react-dates/lib/css/_datepicker.css';

import {
  DialogAlert,
  FormHelpText,
  spacer64,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import {
  DateRangePicker,
  FocusedInputShape,
} from '@spotify-internal/react-dates';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import {
  DEFAULT_DATE_FORMAT,
  fromISOToMomentObject,
  fromMomentObjectToISO,
} from '@helpers/dateTimeUtils';
import { getLanguagePreferenceAsMomentLocale } from '@helpers/translate';
import { isEmptyString } from '@helpers/utils';
import useOnClickOutside from '../helpers/useOnClickOutside';
import { Action, ACTION_TYPES } from '../reducer';
import { DATE_RANGE_TYPE } from '../types';

// Can not style the DatePickerRange itself - see https://github.com/airbnb/react-dates/issues/1030
const StyledDatePicker = styled.div`
  position: absolute;
`;

const StyledDialogAlert = styled(DialogAlert)`
  height: 530px;
  width: 660px;
  margin-top: ${spacer64};
  position: absolute;
  border-radius: 4px;
`;

const StyledHelpText = styled(FormHelpText)<{ renderLower: boolean }>`
  position: absolute;
  margin-top: ${({ renderLower }) => (renderLower ? '85px' : '60px')};
`;

interface DateRangePickerWrapperProps {
  onCloseModal: () => void;
  dispatch: Dispatch<Action>;
}

export const DateRangePickerWrapper = ({
  onCloseModal,
  dispatch,
}: DateRangePickerWrapperProps) => {
  moment.locale(getLanguagePreferenceAsMomentLocale());
  const t = useT();

  const startDateName = 'startDate';
  const endDateName = 'endDate';

  const [endDateField, endDateMeta, endDateHelpers] =
    useField<string>(endDateName);
  const [startDateField, startDateMeta, startDateHelpers] =
    useField<string>(startDateName);

  const handleDatesChange = useCallback(
    ({ startDate: startDateValue, endDate: endDateValue }) => {
      startDateHelpers.setValue(fromMomentObjectToISO(startDateValue) || '');
      endDateHelpers.setValue(fromMomentObjectToISO(endDateValue) || '');
    },
    [startDateHelpers, endDateHelpers],
  );

  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    'startDate',
  );

  const handleClickOutside = useCallback(() => {
    onCloseModal();
    sendEvent({
      eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM_DATE_PICKER,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Closed date picker dialog',
      product: GA_PRODUCT_CATALOG,
    });
    dispatch({
      type: ACTION_TYPES.SELECT_CUSTOM_DATES,
      payload: {
        dateRangeType: DATE_RANGE_TYPE.CUSTOM_DATES,
        dateRange: {
          startDate: startDateField.value,
          endDate: endDateField.value,
        },
        filename: `${startDateField.value} — ${endDateField.value}`,
        customDate: {
          startDate: startDateField.value
            ? fromISOToMomentObject(startDateField.value)
            : null,
          endDate: endDateField.value
            ? fromISOToMomentObject(endDateField.value)
            : null,
        },
      },
    });
  }, [dispatch, endDateField.value, startDateField.value, onCloseModal]);

  const ref = useRef(null);
  useOnClickOutside(ref, () => handleClickOutside());

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCloseModal();
    }
  };

  const touched = startDateMeta.touched || endDateMeta.touched;
  const hasStartDateError =
    !!startDateMeta.error && isEmptyString(startDateMeta.value) && touched;
  const hasEndDateError =
    !!endDateMeta.error && isEmptyString(endDateMeta.value) && touched;

  const yesterday = moment().subtract(1, 'day');

  return (
    <FocusLock returnFocus>
      <StyledDialogAlert
        ref={ref}
        onKeyDownCapture={onKeyDown}
        aria-label="Date range picker"
        body={
          <FocusLock>
            <StyledDatePicker>
              <DateRangePicker
                keepFocusOnInput
                startDatePlaceholderText={t(
                  'atlas.labels.start_date',
                  'Start date',
                  'Label text for start date (i.e. 2022-04-12 00:00:00)',
                )}
                endDatePlaceholderText={t(
                  'atlas.labels.end_date',
                  'End date',
                  'Label text for end date (i.e. 2022-08-12 00:00:00)',
                )}
                startDateId="date-range-start"
                endDateId="date-range-end"
                startDate={fromISOToMomentObject(startDateField.value)}
                endDate={fromISOToMomentObject(endDateField.value)}
                onDatesChange={handleDatesChange}
                minimumNights={0}
                isOutsideRange={(d: moment.Moment) =>
                  d.isAfter(moment(yesterday))
                }
                maxDate={yesterday}
                initialVisibleMonth={() =>
                  endDateField.value
                    ? moment(endDateField.value).subtract(1, 'month')
                    : moment().subtract(1, 'month')
                }
                displayFormat={DEFAULT_DATE_FORMAT.toUpperCase()}
                focusedInput={focusedInput}
                onFocusChange={(
                  focus: SetStateAction<FocusedInputShape | null>,
                ) => {
                  if (!focus) return;
                  setFocusedInput(focus);
                }}
                keepOpenOnDateSelect
                verticalSpacing={hasStartDateError && hasEndDateError ? 80 : 60}
              />
            </StyledDatePicker>
            {hasStartDateError && (
              <StyledHelpText error renderLower={false}>
                {startDateMeta.error}
              </StyledHelpText>
            )}
            {hasEndDateError && (
              <StyledHelpText
                error
                renderLower={hasStartDateError && hasEndDateError}
              >
                {endDateMeta.error}
              </StyledHelpText>
            )}
          </FocusLock>
        }
      />
    </FocusLock>
  );
};
